import { Suspense, useEffect, useRef, useState } from "react";
import { installState, refreshPushToken } from "./notifications";
import { permissionNow, shouldAsk, skippedAt } from "./gate";
import { watchForUpdates } from "./resync";
import { watchForErrors } from "./errors";
import {
  useDay,
  useLimits,
  useMemories,
  useKeyboardUp,
  useMembership,
  useMessages,
  useSavedDailies,
  useToday,
  useReadWhileOpen,
  useUnread,
  useUnseenDailies,
  useUsers,
  usePinned,
  useRecap,
} from "./hooks";
import { go, goBack, swap, useRoute } from "./router";
import { useWide } from "./wide";
import { Bento } from "./Bento";
import { RecapView } from "./screens/Recap";
import { badgeFor, showBadge, titleFor, type BadgeHost } from "./badge";
import { useClearNotifications } from "./clearNotifications";
import { answeredHere, isLocked, unlockedPosts } from "./giviSnapLock";
import { startGiviSnap } from "./screens/Camera";
import { Booting } from "./Booting";
import { urlFor } from "./data";
import { warmMedia, worthWarming } from "./warm";
import {
  APP_NAME,
  CHAT_NAME,
  DAILY_NAME,
  answeredPrompt,
} from "../../shared/constants";
import { SnapSheet } from "./sheets/SnapSheet";
import { Chat } from "./screens/Chat";
import { InstallGate, NotAllowed, SignIn } from "./screens/Onboarding";
import { Today } from "./screens/Today";
import { lazyScreen } from "./lazyScreen";

/*
 * Fetched when first opened, not with the app: see ./lazyScreen. The chat,
 * GiviSnaps and the snap sheet stay in the first file -- they are on screen
 * the moment the app is, or a tap away from it.
 */
const Admin = lazyScreen(() => import("./screens/Admin"), (m) => m.Admin);
const Camera = lazyScreen(() => import("./screens/Camera"), (m) => m.Camera);
const Gallery = lazyScreen(() => import("./screens/Gallery"), (m) => m.Gallery);
const Memories = lazyScreen(() => import("./screens/Memories"), (m) => m.Memories);
const Pager = lazyScreen(() => import("./screens/Pager"), (m) => m.Pager);
const Search = lazyScreen(() => import("./screens/Search"), (m) => m.Search);
const Settings = lazyScreen(() => import("./screens/Settings"), (m) => m.Settings);

/** While one of those comes in: a moment, and from the phone's own copy. */
function Loading() {
  return (
    <div className="center grow">
      <Spinner />
    </div>
  );
}
import { UploadStatus } from "./UploadStatus";
import { PhotoPrompt } from "./PhotoPrompt";
import { markBooted } from "./Crash";
import { StreakNudge, runAtRisk } from "./StreakNudge";
import {
  IconAperture,
  IconCamera,
  IconMemory,
  Spinner,
} from "./ui";

const INSTALL_DISMISSED = "givisnap:installDismissed";
const NOTIFY_DISMISSED = "givisnap:notifyDismissed";

/** The latest "Skip for now" this browser remembers, under either old key. */
function lastSkip(): number | null {
  try {
    const marks = [NOTIFY_DISMISSED, INSTALL_DISMISSED]
      .map((key) => skippedAt(localStorage.getItem(key)))
      .filter((at): at is number => at !== null);
    return marks.length ? Math.max(...marks) : null;
  } catch {
    return null;
  }
}

function rememberSkip(): void {
  try {
    localStorage.setItem(NOTIFY_DISMISSED, String(Date.now()));
  } catch {
    // Nothing to do; the gate reappears next launch.
  }
}

export function App() {
  const membership = useMembership();
  const route = useRoute();

  // Drawn: from here an error is reported, never allowed to wipe the app.
  useEffect(() => markBooted(), []);

  /* The snap sheet, which the camera key opens over whatever is on screen.
     Held here because four things open it: the key, the chat's own camera
     button, and the Post and Add buttons on the GiviSnaps screen. */
  const [snapOpen, setSnapOpen] = useState(false);

  const [gateDone, setGateDone] = useState(
    () =>
      !shouldAsk({
        installed: installState() === "installed",
        permission: permissionNow(),
        skipped: lastSkip(),
        now: Date.now(),
      }),
  );

  // Opened from the profile page when the phone turned notifications down.
  const [pushRefused, setPushRefused] = useState(false);

  const isMember = membership.state === "member";
  const uid = isMember ? membership.user.uid : "";

  const users = useUsers(isMember);
  const pinned = usePinned(isMember);
  // The Sunday recap that is out, if one is: shared/recap.ts.
  const recap = useRecap(isMember);
  const limits = useLimits(isMember);
  const chat = useMessages(isMember);
  const today = useToday(isMember);
  const unread = useUnread(chat.messages, uid);
  const unseenDailies = useUnseenDailies(today.posts, uid);

  /*
   * The same news outside the window: a count in the tab title, and a badge on
   * the icon of an installed app. The service worker puts a dot there when a
   * push lands with the app closed; once it is open, this is what keeps it to
   * the real count, and clears it when there is nothing left.
   */
  const badge = badgeFor(unread.count, unseenDailies.any);
  useEffect(() => {
    document.title = titleFor(badge);
    void showBadge(navigator as BadgeHost, badge);
  }, [badge]);
  // Above the early returns with the rest of them: an effect registers during
  // a render whichever branch that render took.
  const keyboardUp = useKeyboardUp();

  // The archive is a few megabytes, so it is only read once the tab that wants
  // it has actually been opened.
  const memories = useMemories(isMember && route.name === "memories");
  // Kept posts, live, for the same tab. A subscription rather than a read,
  // because pressing keep on a photo should put it there while you watch.
  const saved = useSavedDailies(isMember && route.name === "memories");

  // A pointer in chat can reference an earlier day, which watchToday does not
  // cover; only subscribe to that day when it is actually being viewed.
  const viewingDay = route.name === "daily" ? route.day : null;
  // Against the day on screen, not the calendar one: before the prompt fires
  // those differ, and comparing to the calendar day made the day already being
  // watched look like an archive and opened a second listener onto it.
  const archiveDay =
    viewingDay && viewingDay !== today.showing ? viewingDay : null;
  const archive = useDay(archiveDay, isMember);

  /*
   * Which tab a full-screen photo goes back to when it is closed.
   *
   * Its route says which photo, not where you were standing when you opened
   * it, so closing one always went to the same fixed place — and a kept photo
   * opened from Memories dropped you on GiviSnaps, which is neither where you
   * were nor where the photo lives.
   *
   * A ref rather than state: nothing renders differently for it, and writing
   * it during the render that leaves the tab would be writing it too late.
   */
  const cameFrom = useRef<"today" | "memories" | "camera" | "chat">("today");
  useEffect(() => {
    // Chat among them: most of the pointers into a GiviSnap are in the thread,
    // and closing one dropped you on a tab you had not been near.
    if (
      route.name === "today" ||
      route.name === "memories" ||
      route.name === "camera" ||
      route.name === "chat"
    ) {
      cameFrom.current = route.name;
    }
  }, [route.name]);

  /*
   * Above every early return, because an effect below reads them.
   *
   * These were declared further down, past the loading and sign-in branches.
   * An effect is registered during a render and runs after it whichever branch
   * that render took — so on a launch that came back "still loading", the
   * effect ran against a const its own render had never reached, and the app
   * died on start with a ReferenceError. Only on the camera route, which is
   * the one the reader of a story is on, and reloading is what the update
   * button does.
   */
  const windowEndsAt =
    (
      today.daily?.windowEndsAt as { toMillis?: () => number } | undefined
    )?.toMillis?.() ?? null;

  // Answering the prompt, not a story: an extra must never make someone look
  // done, or the rail and the streak would both start lying. Nor may last
  // night's: the screen holds two days so nothing vanishes when the prompt
  // fires, but only one of them is the prompt being answered.
  const myPost = today.posts.find(
    (post) =>
      post.data.authorUid === uid &&
      post.data.kind !== "extra" &&
      post.day === today.showing,
  );
  const postedAt =
    (
      myPost?.data.createdAt as { toMillis?: () => number } | undefined
    )?.toMillis?.() ?? null;

  /*
   * One GiviSnap per prompt, and only while a prompt is waiting to be answered.
   *
   * It used to be open all day, which made the timer decoration: you could
   * answer at nine in the morning for a prompt that fired at nine at night.
   * Photo and Video are already there for posting whenever, so the prompted
   * one is the only thing the clock has any hold over.
   *
   * The gate is the prompt rather than the window. Before today's fires, the
   * live prompt is still yesterday's — which is the grace period the streak
   * cutoff depends on, so closing at midnight would take it back.
   *
   * The day on screen is already the streak day — watchToday works it out to
   * decide which posts to show, and deriving it a second time here would be
   * two places to keep in step.
   */
  const promptOpen = !answeredPrompt(today.posts, uid, today.showing);

  // Everyone else's GiviSnaps wait until you have posted yours.
  const giviSnapLocked = isLocked({
    settled: today.settled,
    userLoaded: Boolean(users[uid]),
    promptOpen,
    answeredHere: answeredHere(today.showing),
  });
  const visibleToday = unlockedPosts(today.posts, uid, giviSnapLocked);

  /*
   * The camera, opened straight from the GiviSnap tab or a frosted card. The
   * snap sheet has its own; this one is for when there is no sheet in the way,
   * and it has to be clicked inside the tap itself or the phone refuses.
   */
  const giviSnapInput = useRef<HTMLInputElement>(null);
  const takeGiviSnap = () => giviSnapInput.current?.click();

  /*
   * A desktop shows the chat and the GiviSnaps at once: the chat in the pane
   * and the GiviSnaps as tiles beside it. There is no tab bar to move between
   * them, so the GiviSnaps page itself is never the pane there -- landing on
   * it, or coming back to it from a photo, is the chat.
   */
  const wide = useWide();
  useEffect(() => {
    if (wide && route.name === "today") swap("chat");
  }, [wide, route.name]);

  // Being on the chat, with the app in view, is what clears the badge.
  useReadWhileOpen(route.name === "chat", chat.messages, unread.markRead);
  // And what takes its notifications off the phone, as the GiviSnap page does its own.
  useClearNotifications(route.name, isMember);

  /*
   * Fetch today's pictures before anybody asks for one.
   *
   * The app opens on the same few dozen images every time, and until they are
   * in the media cache each is a round trip to the gateway made at the moment
   * somebody is waiting on it. Asked for while nothing else is happening they
   * cost nothing anyone notices, and the screen that wanted them finds them
   * already there.
   */
  useEffect(() => {
    if (!isMember) return;
    const media = worthWarming(
      // Frosted ones are not fetched until they are there to be seen.
      visibleToday.map((post) => post.data),
      chat.messages.map((message) => message.data),
    );
    warmMedia(media.map((each) => urlFor(each)));
  }, [isMember, visibleToday.length, chat.messages.length]);

  // Keeps this browser's one row pointed at whatever token FCM has issued it
  // lately. Without it a rotation left the old row behind, still subscribed,
  // and every push arrived twice.
  useEffect(() => {
    if (isMember && uid) void refreshPushToken(uid);
  }, [isMember, uid]);

  /*
   * Writes down anything nobody caught, so an admin can read it later.
   *
   * Only once there is a member to attribute it to: index.html already catches
   * what throws before the modules load and draws it on the screen, because at
   * that point there is no database to write to and nobody signed in to own
   * it. This is everything after that -- which is the part that has always
   * been invisible, because the console is on somebody else's phone.
   */
  useEffect(() => {
    if (!isMember || !uid) return;
    return watchForErrors(uid);
  }, [isMember, uid]);

  // Asks for a new build on launch and every minute after, rather than only
  // on the way back into the app. One found mid-use is offered, not taken.
  const [newBuild, setNewBuild] = useState(false);
  useEffect(() => watchForUpdates(() => setNewBuild(true)), []);

  // The same mark the page was already showing, so nothing blinks between the
  // bundle arriving and this knowing who is signed in.
  if (membership.state === "loading") return <Booting />;

  if (membership.state === "signed-out") return <SignIn />;
  if (membership.state === "not-a-member")
    return <NotAllowed email={membership.email} />;

  // Shown after sign-in, because push is most of the point. It comes back on
  // the first launch from the Home Screen icon: iOS only grants notification
  // permission to the installed app, and that app gets its own storage, so the
  // dismissal from Safari neither carries over nor should it. By then step one
  // is already done, leaving only the ask that could not be made before.
  // Skipped, it comes back a day later until notifications are on (./gate).
  if (!gateDone) {
    return (
      <InstallGate
        uid={uid}
        onDone={() => {
          setGateDone(true);
          rememberSkip();
        }}
      />
    );
  }

  // Asked for, so leaving it is not a skip, and it goes back to the profile.
  if (pushRefused) {
    return <InstallGate uid={uid} refused onDone={() => setPushRefused(false)} />;
  }

  return (
    <div className="app">
      {/*
       * Every screen renders a header, a scroller and sometimes a composer as
       * siblings, so on a phone they were simply the app's own column. A
       * sidebar needs them gathered into one: the pane is that column, and
       * the navigation sits beside it rather than inside it.
       */}
      <div className="pane">
        <Suspense fallback={<Loading />}>
        {route.name === "admin" ? (
          <Admin uid={uid} />
        ) : route.name === "search" ? (
          <Search
            uid={uid}
            users={users}
            onClose={() => goBack("chat")}
            onOpen={(message) => go(`chat/${message.id}`)}
          />
        ) : route.name === "gallery" ? (
          <Gallery users={users} onClose={() => goBack("chat")} />
        ) : route.name === "you" ? (
          <Settings
            uid={uid}
            user={users[uid]}
            isAdmin={membership.isAdmin}
            onPushRefused={() => setPushRefused(true)}
          />
        ) : route.name === "memories" ? (
          <Memories users={users} memories={memories} saved={saved} />
        ) : route.name === "camera" ? (
          <Camera
            today={today}
            windowEndsAt={windowEndsAt}
            postedAt={postedAt}
          />
        ) : route.name === "chat" ? (
          <Chat
            uid={uid}
            users={users}
            chat={chat}
            limits={limits}
            focus={route.name === "chat" ? route.focus : undefined}
            replyToFocus={route.name === "chat" ? route.reply : undefined}
            photoPrompt={<PhotoPrompt uid={uid} user={users[uid]} />}
            pinned={pinned}
            streakNudge={
              <StreakNudge
                days={runAtRisk(users[uid], today.showing, promptOpen)}
                streakDay={today.showing}
                onPost={() => setSnapOpen(true)}
              />
            }
          />
        ) : (
          <Today
            uid={uid}
            users={users}
            today={today}
            onOpenCamera={() => setSnapOpen(true)}
            locked={giviSnapLocked}
            onTakeGiviSnap={takeGiviSnap}
            recap={recap}
          />
        )}
        </Suspense>
      </div>

      {wide &&
        route.name !== "camera" &&
        route.name !== "admin" &&
        route.name !== "search" &&
        route.name !== "gallery" && (
          <Bento
            uid={uid}
            users={users}
            today={today}
            locked={giviSnapLocked}
            promptOpen={promptOpen}
            windowEndsAt={windowEndsAt}
            onTakeGiviSnap={takeGiviSnap}
            onSnap={() => setSnapOpen(true)}
            onMems={route.name === "memories"}
            recap={recap}
          />
        )}

      {/* Found while the app was being used, so it waits to be asked. */}
      {newBuild && (
        <button className="new-build" onClick={() => window.location.reload()}>
          A newer {APP_NAME} is ready · tap to load it
        </button>
      )}

      <UploadStatus />

      {/*
       * Out of the way while the keyboard is up. Five tabs under a composer
       * on a phone leaves almost nothing of the conversation being replied
       * to, and nobody changes tab mid-sentence.
       */}
      {/*
       * Nor while a GiviSnap is being taken: the photo fills the screen and
       * the camera key, standing proud of the bar, was cut off by it. Nothing
       * on the bar is wanted mid-shot, and Start over is the way out.
       */}
      {/* Not on a desktop, where the tiles beside the chat stand in for it. */}
      {!wide &&
        !keyboardUp &&
        route.name !== "camera" &&
        route.name !== "admin" &&
        route.name !== "search" &&
        route.name !== "gallery" && (
          <nav className="tabbar" aria-label="Main">
            <button
              className="tab"
              aria-current={route.name === "today" ? "page" : undefined}
              onClick={() => {
                go("today");
                // Not posted yet: straight into taking one, with the frosted
                // page underneath for anyone who backs out of the camera.
                if (giviSnapLocked) takeGiviSnap();
              }}
            >
              <span style={{ position: "relative" }}>
                <IconAperture size={22} />
                {unseenDailies.any && <span className="tab-dot" />}
              </span>
              {DAILY_NAME}s
            </button>
            {/*
             * One button that changes job, and the middle of the app.
             *
             * Everywhere else it is the way to the chat, which is where the
             * family talks in photos now, so the chat's unread count rides on
             * it. On the chat it opens the snap sheet over the thread, so the
             * chat is one tap from a snap. It used to open a camera page with
             * a rail of modes and rise into it as the shutter; with stories
             * gone that page was the rail on an empty screen, and the sheet is
             * the snap back's own, pointed at everyone.
             */}
            <button
              className="camera-key"
              aria-label={route.name === "chat" ? "Take a snap" : CHAT_NAME}
              aria-current={route.name === "chat" ? "page" : undefined}
              aria-haspopup={route.name === "chat" ? "dialog" : undefined}
              onClick={() => {
                if (route.name === "chat") setSnapOpen(true);
                else go("chat");
              }}
            >
              <span className="camera-key-icon">
                <IconCamera size={26} />
              </span>
              {/* Named under it like the tabs either side, so it reads as the
                  snap camera and not the composer's, which keeps its pictures. */}
              <span className="camera-key-label" aria-hidden="true">
                {route.name === "chat" ? "Snap" : CHAT_NAME}
              </span>
              {/* Off the chat only: there the count is what you are already
                  reading. (The camera has no bar to carry it.) */}
              {unread.count > 0 && route.name !== "chat" && (
                <span className="tab-badge key-badge">
                  {unread.count > 9 ? "9+" : unread.count}
                </span>
              )}
            </button>

            <button
              className="tab"
              aria-current={route.name === "memories" ? "page" : undefined}
              onClick={() => go("memories")}
            >
              <IconMemory size={22} />
              Mems
            </button>

          </nav>
        )}

      <SnapSheet
        open={snapOpen}
        onClose={() => setSnapOpen(false)}
        promptOpen={promptOpen}
        windowEndsAt={windowEndsAt}
      />
      <input
        ref={giviSnapInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="givisnap-input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // The same picture twice in a row must still say it changed.
          event.target.value = "";
          if (!file || !file.type.startsWith("image/")) return;
          startGiviSnap(file);
          go("camera");
        }}
      />

      {/* The week's collage, over everything; where Sunday's notification lands. */}
      {route.name === "recap" && recap && <RecapView recap={recap} users={users} />}

      {route.name === "daily" && (
        <Suspense fallback={null}>
        <Pager
          uid={uid}
          day={route.day}
          startUid={route.uid}
          startOnComments={route.comments}
          users={users}
          posts={archiveDay ? (archive?.posts ?? []) : visibleToday}
          settled={archiveDay ? (archive?.settled ?? false) : today.settled}
          // Back to where it was opened from, which the route already says.
          onClose={() => goBack(cameFrom.current)}
        />
        </Suspense>
      )}
    </div>
  );
}
